import { useEffect, useMemo, useState } from 'react'
import {
  FieldErrors,
  UseFormClearErrors,
  UseFormSetError,
  UseFormSetValue,
  UseFormTrigger,
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { ComboboxItem } from '@node-space/ui-components'
import { useRoutingNumberValidation } from 'hooks/queries/Beneficiaries/useRoutingNumberValidation'
import { useBicValidation } from 'hooks/queries/useBicValidation'
import { useCountriesQuery } from 'hooks/queries/useCountriesQuery'
import { useIbanValidation } from 'hooks/queries/useIbanValidation'
import { useScanValidation } from 'hooks/queries/useScanValidation'
import { useSouthAfricanBanks } from 'hooks/queries/useSouthAfricanBanks'
import { useMappedErrorMessage } from 'hooks/useMappedErrorMessage'
import { ContactFiatInstrumentFormData } from 'pages/Contacts/schemas/contactFiatInstrumentSchema'
import { FieldsFetchedFromIbanValidation } from 'pages/Contacts/types/fiatInstruments'
import { getIsValidIban, resetBankAddress } from 'pages/Contacts/utils/fiatInstruments'
import { BeneficiaryDetails, BeneficiaryDetailsCurrency, PaymentMethods } from 'types/beneficiaries'
import { isIbanFormat } from 'utils/beneficiaries'
import { titleCase } from 'utils/utils'
import { useBankDetailValidation } from './useBankDetailsValidation'
import { useFormFieldOptions } from './useFiatFormFieldOptions'

interface UseBankDetailsFormProps {
  formValues: ContactFiatInstrumentFormData
  errors: FieldErrors<ContactFiatInstrumentFormData>
  clearErrors: UseFormClearErrors<ContactFiatInstrumentFormData>
  setValue: UseFormSetValue<ContactFiatInstrumentFormData>
  trigger: UseFormTrigger<ContactFiatInstrumentFormData>
  setError: UseFormSetError<ContactFiatInstrumentFormData>
  contactBeneficiary?: BeneficiaryDetails
}

export const useBankDetailsForm = ({
  formValues,
  errors,
  clearErrors,
  setValue,
  trigger,
  setError,
  contactBeneficiary,
}: UseBankDetailsFormProps) => {
  const { t } = useTranslation()
  const { setRequestError } = useMappedErrorMessage()
  const { accountTypeOptions } = useFormFieldOptions({ formValues })

  // State
  const [isValidIban, setIsValidIban] = useState<boolean>(false)
  const [isValidScan, setIsValidScan] = useState<boolean>(false)
  const [isValidRoutingNumber, setIsValidRoutingNumber] = useState<boolean>(false)
  const [isValidBic, setIsValidBic] = useState<boolean>(false)

  // Currency and transfer type checks
  const isUsd = formValues?.currency === BeneficiaryDetailsCurrency.USD
  const isZar = formValues?.currency === BeneficiaryDetailsCurrency.ZAR
  const isGBP = formValues?.currency === BeneficiaryDetailsCurrency.GBP
  const isEur = formValues?.currency === BeneficiaryDetailsCurrency.EUR
  const isInternational = formValues?.paymentMethods?.includes(PaymentMethods.SWIFT)
  const isLocal = !isInternational && !!formValues?.paymentMethods?.length
  const isIban = isIbanFormat(formValues?.accountNumber)
  const isCountryUs = formValues?.bankCountryCode === 'US'
  const isRoutingNumberVisible = isUsd && isInternational && isCountryUs

  // Validation hooks
  const {
    refetch: validateIban,
    isFetching: isValidatingIban,
    isError: isValidatingIbanError,
    data: ibanBankDetails,
  } = useIbanValidation(formValues?.accountNumber, 'IBAN', setRequestError, false)

  const {
    refetch: validateScan,
    isFetching: isValidatingScan,
    isError: isValidatingScanError,
    data: scanBankDetails,
  } = useScanValidation(formValues?.bankCode, formValues?.accountNumber, setRequestError, false)

  const {
    refetch: validateRoutingNumber,
    isFetching: isValidatingRoutingNumber,
    isError: isValidatingRoutingNumberError,
    data: routingNumberBankDetails,
  } = useRoutingNumberValidation(
    isLocal ? formValues?.bankCode : formValues?.routingNumber,
    setRequestError,
    false
  )

  const {
    refetch: validateBic,
    isFetching: isValidatingBic,
    isError: isValidatingBicError,
    data: bicValidationData,
    error: bicValidationError,
  } = useBicValidation(formValues?.bankCode, setRequestError, false)

  const { data: countries, isPending: countriesLoading } = useCountriesQuery(isInternational)

  const {
    data: southAfricanBanks,
    isFetching: isFetchingSouthAfricanBanks,
    isError: isSouthAfricanBanksError,
  } = useSouthAfricanBanks(setRequestError, isZar)

  const isBankDetailsValidationLoading = isValidatingIban || isValidatingScan
  const scanSupportedPaymentMethods = scanBankDetails?.supportedPaymentMethods

  // Unified bank detail validation helper
  const updateFieldValue = (field: keyof FieldsFetchedFromIbanValidation, value: string) => {
    setValue(field, value)
  }

  const setIbanBankDetailsData = (details: FieldsFetchedFromIbanValidation) => {
    if (isLocal && details?.bankCode) {
      setValue('bankCode', details.bankCode)
    } else if (isInternational && Object.keys(details || {})?.length) {
      Object.keys(details)?.forEach(detail => {
        if (detail === 'bankCountry') {
          onInternationalCountrySelection(details[detail])
        } else {
          updateFieldValue(detail as keyof FieldsFetchedFromIbanValidation, details[detail])
        }
      })
    }
  }

  // Bank detail validation hook
  useBankDetailValidation({
    formValues,
    setValue,
    setError,
    clearErrors,
    ibanValidationProps: {
      isValidatingIbanError,
      ibanBankDetails,
      setIsValidIban,
      setIbanBankDetailsData,
    },
    routingNumberValidationProps: {
      isValidatingRoutingNumberError,
      routingNumberBankDetails,
      setIsRoutingNumberValid: setIsValidRoutingNumber,
    },
    scanValidationProps: {
      isValidatingScanError,
      scanBankDetails,
      setIsValidScan,
    },
    ...(isInternational && {
      bicValidationProps: {
        isValidatingBicError,
        bicValidationData,
        bicValidationError,
        setIsValidBic,
      },
    }),
    contactBeneficiary,
    ...(isInternational && {
      isIbanFormat: isIban,
      isValidIban,
    }),
  })

  // Effects
  useEffect(() => {
    if (contactBeneficiary || !isLocal) return
    const isFPSandChapsSupported =
      scanSupportedPaymentMethods?.includes('CHAPS') ||
      scanSupportedPaymentMethods?.includes('BACS') ||
      scanSupportedPaymentMethods?.includes('FASTER_PAYMENT') ||
      scanSupportedPaymentMethods?.includes('CCC_PAYMENTS')

    if (scanBankDetails && !isFPSandChapsSupported && isGBP) {
      setError('accountNumber', { message: t('reachabilityCheckErrorMessage') })
      setError('bankCode', { message: t('reachabilityCheckErrorMessage') })
    }
  }, [scanBankDetails, contactBeneficiary, isLocal, isGBP])

  useEffect(() => {
    if (contactBeneficiary || !isLocal || !isEur) return

    clearErrors(['accountNumber'])

    if (!isBankDetailsValidationLoading && ibanBankDetails) {
      const isValidIban = getIsValidIban({
        currency: formValues?.currency,
        ibanBankDetails,
      })
      setIsValidIban(isValidIban)

      if (!isValidIban) {
        setError('accountNumber', { message: t('ibanValidationError') })
        setValue('bankCode', '')
        return
      }
      setValue('bankCode', ibanBankDetails?.bankAccount?.bankCode)
    }
  }, [
    ibanBankDetails,
    isValidatingIbanError,
    isBankDetailsValidationLoading,
    contactBeneficiary,
    isLocal,
    isEur,
  ])

  useEffect(() => {
    if (contactBeneficiary || !isLocal || !isUsd) return
    if (!routingNumberBankDetails) return

    const { bankDetails, validationFailed } = routingNumberBankDetails
    const { bankAddress } = bankDetails ?? {}

    const isValid = !!bankAddress && !validationFailed?.length
    setIsValidRoutingNumber(isValid)

    if (isValid) {
      const { addressLine1, addressLine2, city, region, postCode } = bankAddress || {}
      setValue('bankAddressLine1', addressLine1)
      setValue('bankAddressLine2', addressLine2)
      setValue('bankCity', city)
      setValue('bankRegion', region)
      setValue('bankPostCode', postCode)
    } else {
      setError('bankCode', { message: t('abaRoutingNumberError') })
      resetLocalBankAddress()
    }
  }, [routingNumberBankDetails, formValues?.currency, contactBeneficiary, isLocal, isUsd])

  useEffect(() => {
    if (!isInternational) return
    if (isValidatingRoutingNumberError && !formValues?.routingNumber) {
      resetBankAddress(setValue, 'bank')
    }
  }, [formValues?.routingNumber, isValidatingRoutingNumberError, isInternational])

  useEffect(() => {
    if (!isInternational) return
    if (isValidatingRoutingNumberError && formValues?.routingNumber) {
      setError('routingNumber', { message: t('abaRoutingNumberError') })
    }
  }, [isValidatingRoutingNumberError, formValues?.routingNumber, setError, t, isInternational])

  useEffect(() => {
    clearErrors()
  }, [formValues?.currency, clearErrors])

  useEffect(() => {
    if (!formValues?.bankCode) {
      setValue('bankName', '')
    }
  }, [formValues?.bankCode])

  useEffect(() => {
    if (isValidatingRoutingNumberError && isLocal) {
      setError('bankCode', { message: t('abaRoutingNumberError') })
    }
  }, [isValidatingRoutingNumberError, isLocal])

  // Handlers
  const resetLocalBankAddress = () => {
    setValue('bankName', '')
    setValue('bankAddressLine1', '')
    setValue('bankAddressLine2', '')
    setValue('bankCity', '')
    setValue('bankRegion', '')
    setValue('bankPostCode', '')
  }

  const onFieldUpdate =
    (fieldName: keyof ContactFiatInstrumentFormData) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setValue(fieldName, e.target.value, {
        shouldValidate: true,
        shouldDirty: true,
      })
    }

  const onAccountTypeChange = (value: string) => {
    setValue('accountType', value)
  }

  const handleAccountNumberOnBlur = async () => {
    if (contactBeneficiary) return
    clearErrors()

    const isValid = await trigger('accountNumber')

    if (!isValid) return

    if (isLocal) {
      if (isGBP && !!formValues?.bankCode) {
        validateScan()
        return
      }

      if (isValid && isEur) {
        validateIban()
      }
    } else if (isInternational) {
      if (!formValues?.accountNumber || !isIban) return
      validateIban()
    }
  }

  const handleSortCodeOnBlur = async () => {
    if (contactBeneficiary || !isLocal) return
    if (!formValues?.bankCode) return

    const isAccountNumberAndBankCodeValid = await trigger(['accountNumber', 'bankCode'])
    setIsValidScan(isAccountNumberAndBankCodeValid)

    if (isAccountNumberAndBankCodeValid) {
      validateScan()
    }
  }

  const handleRoutingNumberOnBlur = async () => {
    if (contactBeneficiary) return
    if (formValues?.currency !== BeneficiaryDetailsCurrency.USD) return
    if (!formValues?.bankCode && !formValues?.routingNumber) return

    await validateRoutingNumber()
  }

  const handleBankCodeOnBlur = async () => {
    if (contactBeneficiary) return
    clearErrors()

    if (!formValues?.bankCode) return

    if (isLocal) {
      if (isGBP) {
        const isScanValid = await trigger(['accountNumber', 'bankCode'])
        setIsValidScan(isScanValid)

        if (isScanValid) {
          handleSortCodeOnBlur()
        }
        return
      }

      if (isUsd) {
        handleRoutingNumberOnBlur()
      }

      const isValid = await trigger('bankCode')
      if (!isValid) return
    } else if (isInternational) {
      if (isIbanFormat) return
      validateBic()
    }
  }

  const handleAccountNumberConfirmOnBlur = async () => {
    if (contactBeneficiary) return
    if (!formValues?.accountNumberConfirm) return

    clearErrors(['accountNumber', 'accountNumberConfirm'])

    if (formValues?.accountNumber && formValues?.accountNumberConfirm) {
      await trigger(['accountNumber', 'accountNumberConfirm'])
    }
  }

  const handleInternationalRoutingValidation = async () => {
    if (contactBeneficiary || !isInternational) return
    if (!formValues?.routingNumber) return
    if (formValues?.currency !== BeneficiaryDetailsCurrency.USD) return

    validateRoutingNumber()
  }

  const onInternationalCountrySelection = (countryCode: string) => {
    const countryName = internationalCountryList.find(
      country => country?.value === countryCode
    )?.leftText
    setValue('bankCountryName', countryName)
    setValue('bankCountryCode', countryCode)
  }

  const handleBankOnChange = (bankCode: string) => {
    setValue('bankNameCode', bankCode)
    const bankName = southAfricanBankOptions?.find(bank => bank?.value === bankCode)?.leftText
    if (bankName) {
      setValue('bankName', bankName as string)
    }
  }

  // Computed values
  const southAfricanBankOptions: ComboboxItem[] = useMemo(() => {
    if (!southAfricanBanks?.length) return []
    return southAfricanBanks?.map(bank => ({
      leftText: titleCase(bank?.name),
      value: bank?.id?.toString(),
    }))
  }, [southAfricanBanks])

  const internationalCountryList = useMemo(() => {
    if (countries?.length) {
      return countries?.map(country => ({
        leftText: country?.name,
        value: country?.code,
      }))
    }
    return []
  }, [countries])

  const isValidAccountNumber = useMemo(() => {
    if (isGBP && isLocal) return isValidScan
    if (isEur) return isValidIban
    if (isInternational && isIban) return isValidIban
    return false
  }, [isValidIban, isValidScan, formValues?.currency, isLocal, isInternational, isIban])

  const isValidBankCode = useMemo(() => {
    if (isGBP && isLocal) return isValidScan
    if (isUsd && isLocal) return isValidRoutingNumber
    if (isInternational && isIban) return isValidIban
    if (isInternational && !isIban) return isValidBic
    return false
  }, [
    isValidRoutingNumber,
    isValidScan,
    isValidIban,
    isValidBic,
    formValues?.currency,
    isLocal,
    isInternational,
    isIban,
  ])

  const isAccountNumberAndBankCodeInline = (isLocal && (isEur || isGBP)) || isInternational
  const isAccountTypeRequired = isUsd || isZar

  return {
    // Currency/Transfer flags
    isUsd,
    isZar,
    isGBP,
    isEur,
    isInternational,
    isLocal,
    isIban,
    isCountryUs,
    isRoutingNumberVisible,
    isAccountNumberAndBankCodeInline,
    isAccountTypeRequired,

    // Validation states
    isValidIban,
    isValidScan,
    isValidRoutingNumber,
    isValidBic,
    isValidAccountNumber,
    isValidBankCode,

    // Loading states
    isBankDetailsValidationLoading,
    isValidatingRoutingNumber,
    isValidatingScan,
    isValidatingBic,
    countriesLoading,
    isFetchingSouthAfricanBanks,

    // Error states
    isSouthAfricanBanksError,

    // Options
    accountTypeOptions,
    southAfricanBankOptions,
    internationalCountryList,

    // Handlers
    onFieldUpdate,
    onAccountTypeChange,
    handleAccountNumberOnBlur,
    handleBankCodeOnBlur,
    handleAccountNumberConfirmOnBlur,
    handleInternationalRoutingValidation,
    onInternationalCountrySelection,
    handleBankOnChange,
  }
}
